import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError, fmtBRL, labelOf } from "@/lib/api";
import { useChat } from "@/context/ChatContext";

const EASE = [0.22, 1, 0.36, 1];

const CLASS_META = {
  quente: { label: "Perfil quente", color: "#1FA855", bg: "bg-green-50", text: "text-green-700" },
  morno: { label: "Perfil morno", color: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700" },
  frio: { label: "Perfil frio", color: "#64748B", bg: "bg-slate-100", text: "text-slate-600" },
};

function ScoreRing({ score, classification }) {
  const color = CLASS_META[classification]?.color || "#64748B";
  const c = 2 * Math.PI * 52;
  return (
    <svg width="104" height="104" viewBox="0 0 120 120" role="img" aria-label={`Score ${score} de 100`}>
      <circle cx="60" cy="60" r="52" fill="none" stroke="#E2E8F0" strokeWidth="10" />
      <motion.circle
        cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c * (1 - score / 100) }}
        transition={{ duration: 1.2, ease: EASE }}
        transform="rotate(-90 60 60)"
      />
      <text x="60" y="58" textAnchor="middle" style={{ fontSize: 30, fontWeight: 800 }} className="fill-slate-900 font-display">
        {score}
      </text>
      <text x="60" y="78" textAnchor="middle" style={{ fontSize: 12 }} className="fill-slate-400">
        de 100
      </text>
    </svg>
  );
}

function Typing() {
  return (
    <div className="flex gap-1.5 rounded-3xl rounded-bl-md border border-slate-200 bg-white px-4 py-3.5" data-testid="chat-typing-indicator">
      <span className="typing-dot h-2 w-2 rounded-full bg-brand-400" />
      <span className="typing-dot h-2 w-2 rounded-full bg-brand-400" />
      <span className="typing-dot h-2 w-2 rounded-full bg-brand-400" />
    </div>
  );
}

export default function ChatWidget() {
  const { open, setOpen, preset, trackLead, setPrivacyOpen } = useChat();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("idle");
  const [quick, setQuick] = useState(null);
  const [progress, setProgress] = useState(0);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const startedKey = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy, step, result]);

  useEffect(() => {
    if (open) {
      const key = preset || "geral";
      if (startedKey.current !== key) {
        startedKey.current = key;
        start(preset);
      }
    }
  }, [open, preset]);

  async function start(cat) {
    setBusy(true);
    setError(null);
    setMessages([]);
    setResult(null);
    setConsent(false);
    setStep("loading");
    setQuick(null);
    try {
      const { data } = await api.post("/chat/start", { category: cat || null });
      setSessionId(data.session_id);
      const msgs = [];
      if (cat) msgs.push({ role: "user", text: cat === "imovel" ? "Quero um imóvel" : "Quero um veículo" });
      msgs.push({ role: "bot", text: data.reply });
      setMessages(msgs);
      setStep(data.step);
      setQuick(data.quick_replies);
      setProgress(data.progress);
    } catch (e) {
      setError("Não consegui iniciar o chat agora. Verifique a conexão e tente de novo — ou fale direto pelo WhatsApp.");
      setStep("error");
    } finally {
      setBusy(false);
    }
  }

  async function send(text) {
    if (!text.trim() || busy || !sessionId) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: text.trim() }]);
    setInput("");
    try {
      const { data } = await api.post(`/chat/${sessionId}/message`, { text: text.trim() });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
      setStep(data.step);
      setQuick(data.step === "consent" ? null : data.quick_replies);
      setProgress(data.progress);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: formatApiError(e, "Ops, não consegui responder. Tenta de novo?") }]);
    } finally {
      setBusy(false);
    }
  }

  async function submitConsent() {
    if (!consent || consentBusy) return;
    setConsentBusy(true);
    try {
      const { data } = await api.post(`/chat/${sessionId}/consent`, { consent: true });
      setResult(data);
      setStep("done");
      trackLead(data.summary?.category);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: formatApiError(e, "Ops, algo falhou. Tenta de novo?") }]);
    } finally {
      setConsentBusy(false);
    }
  }

  function goSection(href) {
    setOpen(false);
    setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 150);
  }

  const meta = result ? CLASS_META[result.classification] : null;

  return (
    <>
      <motion.button
        data-testid="chat-fab"
        aria-label={open ? "Fechar chat" : "Abrir chat com a assistente da Isabela"}
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-[5.75rem] right-5 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-brand-600 text-white shadow-2xl shadow-brand-600/40 transition-colors hover:bg-brand-700"
      >
        {!open && <span aria-hidden="true" className="absolute inset-0 rounded-full border-2 border-brand-500 animate-pulse-ring" />}
        {open ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-x-3 bottom-3 top-[4.75rem] z-50 flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl sm:inset-x-auto sm:top-auto sm:right-5 sm:bottom-[5.75rem] sm:h-[620px] sm:w-[400px]"
          >
            <div className="relative bg-brand-800 px-5 py-4 text-white">
              <div className="flex items-center gap-3">
                <img src="/favicon.svg" alt="" className="h-10 w-10 rounded-full border-2 border-white/20" />
                <div className="flex-1 leading-tight">
                  <p className="font-display text-base font-bold">Fale com a Isabela</p>
                  <p className="text-[11px] font-semibold text-brand-200">Assistente virtual · sem compromisso</p>
                </div>
                <button
                  data-testid="chat-close-button"
                  aria-label="Fechar chat"
                  onClick={() => setOpen(false)}
                  className="rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {step !== "done" && step !== "error" && (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/15">
                  <motion.div
                    className="h-full rounded-full bg-brand-300"
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: EASE }}
                    data-testid="chat-progress-bar"
                  />
                </div>
              )}
            </div>

            <div className="chat-scroll flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-3xl px-4 py-3 text-base leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-md bg-brand-600 text-white"
                        : "rounded-bl-md border border-slate-200 bg-white text-slate-700"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && <Typing />}

              {step === "consent" && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl border border-brand-200 bg-white p-4"
                  data-testid="chat-consent-box"
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="chat-consent"
                      data-testid="chat-consent-checkbox"
                      checked={consent}
                      onCheckedChange={(v) => setConsent(!!v)}
                      className="mt-0.5"
                    />
                    <label htmlFor="chat-consent" className="cursor-pointer text-base leading-relaxed text-slate-600">
                      Autorizo o contato da consultora <strong>Isabela Guedes</strong> sobre consórcio, com base
                      no meu consentimento (LGPD).{" "}
                      <button
                        type="button"
                        data-testid="chat-privacy-link"
                        onClick={() => setPrivacyOpen(true)}
                        className="font-bold text-brand-600 underline underline-offset-2"
                      >
                        Política de privacidade
                      </button>
                    </label>
                  </div>
                  <button
                    data-testid="chat-consent-submit"
                    disabled={!consent || consentBusy}
                    onClick={submitConsent}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-5 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {consentBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Ver meu resultado
                  </button>
                </motion.div>
              )}

              {step === "done" && result && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="space-y-4" data-testid="chat-result">
                  <div className="rounded-3xl border border-slate-200 bg-white p-5 text-center">
                    <div className="flex justify-center">
                      <ScoreRing score={result.score} classification={result.classification} />
                    </div>
                    <span className={`mt-3 inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${meta.bg} ${meta.text}`} data-testid="chat-result-badge">
                      {meta.label}
                    </span>

                    {result.classification === "quente" && (
                      <div className="mt-4">
                        <p className="text-base leading-relaxed text-slate-600">
                          Ótima notícia, {result.summary?.name?.split(" ")[0]}: seu perfil tem excelente aderência
                          com o consórcio. A Isabela já foi avisada e pode te atender agora.
                        </p>
                        <a
                          href={result.whatsapp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="chat-result-whatsapp-button"
                          className="mt-4 flex items-center justify-center gap-2 rounded-full bg-wa px-5 py-3.5 text-sm font-bold text-white shadow-lg shadow-wa/30 transition-all hover:-translate-y-0.5"
                        >
                          Falar agora com a Isabela no WhatsApp
                        </a>
                      </div>
                    )}

                    {result.classification === "morno" && (
                      <div className="mt-4 text-left">
                        <p className="text-base leading-relaxed text-slate-600">
                          Você está no caminho, {result.summary?.name?.split(" ")[0]}! Seu perfil foi salvo e a
                          Isabela recebe um resumo diário. Enquanto isso, três dicas:
                        </p>
                        <ul className="mt-3 space-y-2 text-base leading-relaxed text-slate-600">
                          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-brand-600" /> Defina a carta com folga para custos de escritura, registro e eventuais taxas.</li>
                          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-brand-600" /> Mantenha a parcela em até 30% da sua renda mensal para dormir tranquilo.</li>
                          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-brand-600" /> Um valor para lance (ou FGTS, no imóvel) pode antecipar a contemplação.</li>
                        </ul>
                        <a
                          href={result.whatsapp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="chat-result-schedule-button"
                          className="mt-4 flex items-center justify-center gap-2 rounded-full border-2 border-brand-600/20 px-5 py-3 text-sm font-bold text-brand-600 transition-all hover:border-brand-600/50"
                        >
                          Agendar contato por WhatsApp
                        </a>
                      </div>
                    )}

                    {result.classification === "frio" && (
                      <div className="mt-4 text-left">
                        <p className="text-base leading-relaxed text-slate-600">
                          Obrigada pela visita, {result.summary?.name?.split(" ")[0]}! Sem pressa: vale se
                          planejar um pouco mais antes de começar. Aqui tem material para te ajudar:
                        </p>
                        <div className="mt-3 flex flex-col gap-2">
                          <button
                            data-testid="chat-result-link-how"
                            onClick={() => goSection("#como-funciona")}
                            className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-brand-600/40 hover:text-brand-600"
                          >
                            Ver como funciona o consórcio
                          </button>
                          <button
                            data-testid="chat-result-link-faq"
                            onClick={() => goSection("#faq")}
                            className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-brand-600/40 hover:text-brand-600"
                          >
                            Ver dúvidas frequentes
                          </button>
                        </div>
                        <p className="mt-3 text-[11px] text-slate-400">
                          Sem promessa de retorno: quando quiser, recomece por aqui ou pelo WhatsApp.
                        </p>
                      </div>
                    )}

                    <p className="mt-4 text-[10px] leading-relaxed text-slate-400">
                      Simulações e condições sujeitas à análise e às regras do grupo de consórcio vigente.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white p-4 text-base text-slate-500" data-testid="chat-result-summary">
                    <p className="mb-2 font-bold uppercase tracking-wide text-slate-400">Seu perfil</p>
                    <ul className="space-y-1.5">
                      <li>Categoria: <strong className="text-slate-700">{labelOf(result.summary?.category)} · {labelOf(result.summary?.subtype)}</strong></li>
                      <li>Carta desejada: <strong className="text-slate-700">{fmtBRL(result.summary?.credit_value)}</strong></li>
                      <li>Parcela confortável: <strong className="text-slate-700">{fmtBRL(result.summary?.monthly_payment)}</strong></li>
                      <li>Prazo: <strong className="text-slate-700">{labelOf(result.summary?.timeline)}</strong></li>
                    </ul>
                  </div>

                  <button
                    data-testid="chat-result-close"
                    onClick={() => setOpen(false)}
                    className="w-full rounded-full border border-slate-200 px-5 py-3 text-sm font-bold text-slate-500 transition-colors hover:bg-slate-100"
                  >
                    Continuar navegando
                  </button>
                </motion.div>
              )}

              {step === "error" && (
                <div className="rounded-3xl border border-red-100 bg-red-50 p-4 text-sm text-red-600" data-testid="chat-error">
                  {error}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {step !== "consent" && step !== "done" && step !== "error" && (
              <div className="border-t border-slate-200 bg-white px-4 py-3">
                {quick && quick.length > 0 && !busy && (
                  <div className="mb-3 flex flex-wrap gap-2" data-testid="chat-quick-replies">
                    {quick.map((q) => (
                      <button
                        key={q.value}
                        data-testid={`chat-quick-${q.value}`}
                        onClick={() => send(q.label)}
                        className="rounded-full border-2 border-brand-600/20 bg-brand-50 px-4 py-3 text-sm font-bold text-brand-700 transition-all hover:border-brand-600 hover:bg-brand-600 hover:text-white"
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send(input);
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    data-testid="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={step === "name" ? "Digite seu nome..." : "Digite sua resposta..."}
                    className="h-12 flex-1 rounded-full border border-slate-200 bg-slate-50 px-5 text-base outline-none transition-colors focus:border-brand-500 focus:bg-white"
                    aria-label="Sua resposta"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    data-testid="chat-send-button"
                    disabled={busy || !input.trim()}
                    aria-label="Enviar resposta"
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white transition-all hover:bg-brand-700 disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
                <p className="mt-2 px-1 text-[10px] leading-relaxed text-slate-400">
                  Sem compromisso. Valores exatos saem da simulação oficial da Isabela.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
